import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  TicketTemplate,
  UpdateTicketTemplateRequest,
  CreateTicketTemplateRequest,
  DeleteTicketTemplateRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* getTicketTemplatesAsync() {
  yield put({ type: types.GET_TICKET_TEMPLATES.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/ticketTemplate`);
    const response: TicketTemplate[] = yield call(api.getAll);

    yield put({ type: types.GET_TICKET_TEMPLATES.SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.GET_TICKET_TEMPLATES.ERROR, payload: 'Failed to get ticket templates' });
  } finally {
    yield put({ type: types.GET_TICKET_TEMPLATES.PENDING, payload: false });
  }
}

export function* createTicketTemplateAsync({ payload }: CreateTicketTemplateRequest) {
  yield put({ type: types.CREATE_TICKET_TEMPLATE.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/ticketTemplate`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_TICKET_TEMPLATE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_TICKET_TEMPLATE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_TICKET_TEMPLATE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.CREATE_TICKET_TEMPLATE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.CREATE_TICKET_TEMPLATE.PENDING, payload: false });
  }
}

export function* updateTicketTemplateAsync({ payload }: UpdateTicketTemplateRequest) {
  yield put({ type: types.UPDATE_TICKET_TEMPLATE.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/ticketTemplate`);
    const response: TicketTemplate = yield call(api.update, payload);

    yield put({ type: types.UPDATE_TICKET_TEMPLATE.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_TICKET_TEMPLATE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_TICKET_TEMPLATE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.UPDATE_TICKET_TEMPLATE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.UPDATE_TICKET_TEMPLATE.PENDING, payload: false });
  }
}

export function* deleteTicketTemplateAsync({ payload }: DeleteTicketTemplateRequest) {
  yield put({ type: types.DELETE_TICKET_TEMPLATE.PENDING, payload: true });

  try {
    const { id } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/ticketTemplate`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_TICKET_TEMPLATE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_TICKET_TEMPLATE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_TICKET_TEMPLATE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.DELETE_TICKET_TEMPLATE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.DELETE_TICKET_TEMPLATE.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_TICKET_TEMPLATES.REQUEST, getTicketTemplatesAsync),
  takeLatest(types.CREATE_TICKET_TEMPLATE.REQUEST, createTicketTemplateAsync),
  takeLatest(types.UPDATE_TICKET_TEMPLATE.REQUEST, updateTicketTemplateAsync),
  takeLatest(types.DELETE_TICKET_TEMPLATE.REQUEST, deleteTicketTemplateAsync)
];
